import { handleApiError } from "@/app/utils/handleApiError";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchSettings } from "./settings.service";
import { Settings, SettingItem } from "./settings.types";
import { RejectValueType } from "../redux/redux.types";
import { RootState } from "../../store";

const normalizeSettings = (items: SettingItem[]): Settings => {
  const map = new Map(items.map((i) => [i.key, i.value]));
  const get = (key: string) => map.get(key) ?? "";

  return {
    site_title: get("site_title"),
    site_name: get("site_name"),
    site_description: get("site_description"),
    site_logo: get("site_logo"),
    site_dark_logo: get("site_dark_logo"),
    site_favicon: get("site_favicon"),
    meta_title: get("meta_title"),
    meta_robots: get("meta_robots"),
    meta_description: get("meta_description"),
    support_phone: get("support_phone"),
    whatsapp_url: get("whatsapp_url"),
    whatsapp_icon: get("whatsapp_icon"),
    default_country_id: get("default_country_id"),
    cod_cash_fee: get("cod_cash_fee"),
    cod_card_fee: get("cod_card_fee"),
    cod_max_limit: get("cod_max_limit"),
    play_store_url: get("play_store_url"),
    app_store_url: get("app_store_url"),
    play_store_icon: get("play_store_icon"),
    app_store_icon: get("app_store_icon"),
    instagram: get("instagram"),
    facebook: get("facebook"),
    youtube: get("youtube"),
    x: get("x"),
    tumblr: get("tumblr"),
    pinterest: get("pinterest"),
    header_announcements: get("header_announcements"),
    header_top_links: get("header_top_links"),
    footer_logo: get("footer_logo"),
    footer_menu: get("footer_menu"),
    footer_apps: get("footer_apps"),
    footer_social_media: get("footer_social_media"),
    footer_newsletter: get("footer_newsletter"),
    footer_payment_logo: get("footer_payment_logo"),
    footer_copyright: get("footer_copyright"),
    footer_bottom_links: get("footer_bottom_links"),
    gtm_container_id: get("gtm_container_id"),
    ga4_measurement_id: get("ga4_measurement_id"),
    google_ads_conversion_id: get("google_ads_conversion_id"),
    google_ads_purchase_label: get("google_ads_purchase_label"),
    google_ads_add_to_cart_label: get("google_ads_add_to_cart_label"),
    google_ads_begin_checkout_label: get("google_ads_begin_checkout_label"),
    google_ads_view_item_label: get("google_ads_view_item_label"),
    google_site_verification: get("google_site_verification"),
    merchant_center_id: get("merchant_center_id"),
    merchant_center_verification: get("merchant_center_verification"),
    facebook_domain_verification: get("facebook_domain_verification"),
    facebook_pixel_id: get("facebook_pixel_id"),
    tiktok_pixel_id: get("tiktok_pixel_id"),
    clarity_project_id: get("clarity_project_id"),
  };
};

export const getSettings = createAsyncThunk<
  Settings,
  void,
  RejectValueType & { state: RootState }
>(
  "settings/getSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchSettings();
      return normalizeSettings(response.data);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
  {
    condition: (_, { getState }) => {
      const { settings } = getState();
      if (settings.status === "loading") {
        return false;
      }
      return true;
    },
  },
);
